/**
 * BN-aware change detection for account trading data.
 *
 * The sync loop recomputes data every tick, producing new object/BN references
 * even when the underlying values haven't changed. Without these guards,
 * every setQueryData call would notify all consumers and trigger re-renders
 *
 * We compare BN fields with BN.eq() and primitives with ===, and only return
 * true when something actually changed. This keeps setQueryData from firing
 * unless consumers genuinely need to update.
 */

import { BN } from '@velocity-exchange/sdk';
import { MarginInfo } from 'src/stores/driftStoreTypes';
import {
	PositionsResult,
	OrdersResult,
	LeftoverIsolatedPositionsResult,
} from './types';

function arraysEqual(
	a: number[] | undefined,
	b: number[] | undefined
): boolean {
	if (a === b) return true;
	if (!a || !b || a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

function bnEq(a: BN | undefined, b: BN | undefined): boolean {
	if (a === b) return true;
	if (!a || !b) return false;
	return a.eq(b);
}

export function hasMarginInfoChanged(
	prev: MarginInfo | undefined,
	next: MarginInfo
): boolean {
	if (!prev) return true;
	return (
		!bnEq(prev.netUsdValue, next.netUsdValue) ||
		!bnEq(prev.freeCollateral, next.freeCollateral) ||
		!bnEq(prev.totalCollateral, next.totalCollateral) ||
		!bnEq(prev.totalMaintenanceCollateral, next.totalMaintenanceCollateral) ||
		!bnEq(prev.maintenanceFreeCollateral, next.maintenanceFreeCollateral) ||
		!bnEq(prev.collateral, next.collateral) ||
		!bnEq(prev.unrealisedPnL, next.unrealisedPnL) ||
		!bnEq(prev.maintenanceReq, next.maintenanceReq) ||
		!bnEq(prev.initialReq, next.initialReq) ||
		!bnEq(prev.totalUnsettledPnl, next.totalUnsettledPnl) ||
		!bnEq(prev.quoteInOpenOrders, next.quoteInOpenOrders) ||
		prev.leverage !== next.leverage ||
		prev.marginRatioPct !== next.marginRatioPct ||
		!arraysEqual(prev.marketsNeedingSettlement, next.marketsNeedingSettlement)
	);
}

export function hasPositionsChanged(
	prev: PositionsResult | undefined,
	next: PositionsResult
): boolean {
	if (!prev) return true;
	if (prev.openPerpPositions.length !== next.openPerpPositions.length)
		return true;
	if (!bnEq(prev.totalUnsettledPnl, next.totalUnsettledPnl)) return true;
	if (
		!arraysEqual(prev.marketsNeedingSettlement, next.marketsNeedingSettlement)
	)
		return true;

	for (let i = 0; i < next.openPerpPositions.length; i++) {
		const p = prev.openPerpPositions[i];
		const n = next.openPerpPositions[i];
		if (
			p.marketIndex !== n.marketIndex ||
			!p.baseSize.eq(n.baseSize) ||
			!p.notional.eq(n.notional) ||
			!p.entryPrice.eq(n.entryPrice) ||
			!p.unsettledPnl.eq(n.unsettledPnl) ||
			!p.totalUnrealizedPnl.eq(n.totalUnrealizedPnl) ||
			!p.liqPrice.eq(n.liqPrice) ||
			p.direction !== n.direction
		) {
			return true;
		}
	}
	return false;
}

export function hasOrdersChanged(
	prev: OrdersResult | undefined,
	next: OrdersResult
): boolean {
	if (!prev) return true;
	if (prev.openOrders.length !== next.openOrders.length) return true;
	if (!bnEq(prev.quoteInOpenOrders, next.quoteInOpenOrders)) return true;

	for (let i = 0; i < next.openOrders.length; i++) {
		const p = prev.openOrders[i];
		const n = next.openOrders[i];
		if (
			p.orderId !== n.orderId ||
			p.status !== n.status ||
			!p.baseAssetAmountFilled.val.eq(n.baseAssetAmountFilled.val) ||
			!p.quoteAssetAmountFilled.val.eq(n.quoteAssetAmountFilled.val)
		) {
			return true;
		}
	}
	return false;
}

export function hasLeftoverIsolatedChanged(
	prev: LeftoverIsolatedPositionsResult | undefined,
	next: LeftoverIsolatedPositionsResult
): boolean {
	if (!prev) return true;
	if (prev.length !== next.length) return true;

	for (let i = 0; i < next.length; i++) {
		if (
			prev[i].marketIndex !== next[i].marketIndex ||
			!prev[i].isolatedPositionScaledBalance.eq(
				next[i].isolatedPositionScaledBalance
			)
		) {
			return true;
		}
	}
	return false;
}
